import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Mail, CheckCircle2, AlertCircle, Clock, Ban, MousePointer, Eye, ExternalLink, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mapping from internal status to badge style + icon. Covers the full list of
// Resend webhook event types (sent / delivered / opened / clicked / bounced /
// complained / failed / delayed / suppressed / duplicate).
const STATUS_STYLES = {
  sent:        { bg: 'bg-slate-100 text-slate-700',       icon: Clock,          label: 'Sent' },
  queued:      { bg: 'bg-slate-100 text-slate-700',       icon: Clock,          label: 'Queued' },
  delivered:   { bg: 'bg-emerald-50 text-emerald-700',    icon: CheckCircle2,   label: 'Delivered' },
  opened:      { bg: 'bg-sky-50 text-sky-700',            icon: Eye,            label: 'Opened' },
  clicked:     { bg: 'bg-indigo-50 text-indigo-700',      icon: MousePointer,   label: 'Clicked' },
  bounced:     { bg: 'bg-rose-50 text-rose-700',          icon: AlertCircle,    label: 'Bounced' },
  complained:  { bg: 'bg-amber-50 text-amber-800',        icon: AlertCircle,    label: 'Complained' },
  failed:      { bg: 'bg-rose-50 text-rose-700',          icon: AlertCircle,    label: 'Failed' },
  delayed:     { bg: 'bg-amber-50 text-amber-800',        icon: Clock,          label: 'Delayed' },
  suppressed:  { bg: 'bg-slate-200 text-slate-600',       icon: Ban,            label: 'Suppressed' },
  duplicate:   { bg: 'bg-slate-100 text-slate-500',       icon: Ban,            label: 'Duplicate' },
};

const StatusPill = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.sent;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
};

const CATEGORY_LABEL = {
  transactional: 'Transactional',
  notification: 'Notification',
  newsletter: 'Newsletter',
  campaign: 'Campaign',
  system: 'System',
};

/**
 * MemberEmailHistoryModal — admin-side view of every email sent to a member.
 *
 * Opens on top of UsersManagementPage via portal. Fetches `/admin/members/{id}/
 * email-history` and groups events by delivery timeline. Also shows the
 * member's suppression entry (if any) so admins know why marketing is paused.
 */
const MemberEmailHistoryModal = ({ user, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${API}/admin/members/${user.id}/email-history?limit=100`,
          { withCredentials: true },
        );
        setData(data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Could not load email history');
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (!user) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="member-email-history-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[#002249] font-bold text-lg">
              <Mail className="w-5 h-5" /> Email Communications
            </div>
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {user.name} · {user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 flex-shrink-0"
            data-testid="email-history-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Suppression banner */}
        {data?.suppression?.suppressed && (
          <div className="px-5 py-3 bg-rose-50 border-b border-rose-100 flex items-start gap-2.5">
            <Ban className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-rose-900">
              <span className="font-semibold">Marketing is paused</span> for this address
              {data.suppression.scope === 'all' ? ' entirely' : ` (${data.suppression.scope})`}.
              Reason: <span className="font-mono text-xs bg-white px-1.5 py-0.5 rounded">{data.suppression.last_reason}</span>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="max-h-[65vh] overflow-y-auto">
          {loading && (
            <div className="p-8 text-center text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading history…
            </div>
          )}
          {error && (
            <div className="p-6 text-sm text-rose-700 text-center">{error}</div>
          )}
          {!loading && !error && data?.events?.length === 0 && (
            <div className="p-10 text-center text-slate-500 text-sm">
              No email activity recorded yet for this member.
            </div>
          )}
          {!loading && data?.events?.length > 0 && (
            <ul className="divide-y divide-slate-100" data-testid="email-events-list">
              {data.events.map((ev) => (
                <li key={ev.id} className="px-5 py-3 hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-[#1a1a1a] line-clamp-1">
                        {ev.subject || '(no subject)'}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{formatDate(ev.created_at)}</span>
                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                        <span className="font-medium">
                          {CATEGORY_LABEL[ev.category] || ev.category}
                        </span>
                        {ev.source && (
                          <>
                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                            <span className="font-mono text-[11px] text-slate-400">{ev.source}</span>
                          </>
                        )}
                      </div>
                      {ev.error && (
                        <div className="text-xs text-rose-600 mt-1 font-mono truncate" title={ev.error}>
                          {ev.error}
                        </div>
                      )}
                      {ev.status_history && ev.status_history.length > 1 && (
                        <div className="flex items-center flex-wrap gap-1 mt-1.5">
                          {ev.status_history.slice(1).map((h, i) => (
                            <StatusPill key={i} status={h.status} />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1.5">
                      <StatusPill status={ev.last_status || ev.status} />
                      {ev.resend_message_id && (
                        <span className="font-mono text-[10px] text-slate-400 truncate max-w-[120px]"
                              title={ev.resend_message_id}>
                          {ev.resend_message_id.slice(0, 12)}…
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 text-xs text-slate-500 flex items-center justify-between">
          <span>
            Showing {data?.events?.length || 0} most-recent events
          </span>
          <a
            href="/admin/email-communications"
            className="inline-flex items-center gap-1 text-[#002249] font-semibold hover:underline"
          >
            Open Communication Center <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default MemberEmailHistoryModal;
